import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Edit,
  Cancel,
  Business,
  Email, 
  LocationOn,
  Badge,
  VerifiedUser,
  Add,
  Delete,
  TransferWithinAStation,
  Warning
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { apiService } from '../services/api';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auth } from '../firebase';
import { resolveBrandLogo, getVendorInfo } from '../utils/vendorLogos';
import { ConfirmDialog, EmptyState, FormDialog, LoadingState, PageContainer, PageHeader, RegionSelect, StatusChip } from '../components/ui';
import { ValidatedTextField } from '../components/ui/ValidatedTextField';
import { regionCountryCode } from '../utils/regionCountry';
import { validateEmail, validatePhone, normalizePhone, validateUrl, validatePostalCode, normalizePostalCode } from '../utils/inputValidators';
import { SupportContactCard } from '../components/SupportContactCard';
import { GroupInvitationCard } from '../components/GroupInvitationCard';
import { OrganizationJoinRequestsCard } from '../components/OrganizationJoinRequestsCard';
import { StoreLocationsCard } from '../components/StoreLocationsCard';
import { RebateTreatmentCard } from '../components/RebateTreatmentCard';
import { BrandPricingExclusionsCard } from '../components/BrandPricingExclusionsCard';
import { TradeInCreditsCard } from '../components/TradeInCreditsCard';
import { StoreTimezoneCard } from '../components/StoreTimezoneCard';
import { StoreAiCopyRulesCard } from '../components/StoreAiCopyRulesCard';
import { PriceChangeReviewCard } from '../components/PriceChangeReviewCard';
import { SellThroughSharingCard } from '../components/SellThroughSharingCard';
import { ReorderSettingsCard } from '../components/reorder/ReorderSettingsCard';
import { useToast } from '../contexts/ToastContext';
import { tokens } from '../theme/tokens';
import BrandAuthorizationDialog, { BRAND_AUTHORIZATIONS_QUERY_KEY } from '../components/brandAuthorizations/BrandAuthorizationDialog';
import BrandAuthorizationTagChip from '../components/brandAuthorizations/BrandAuthorizationTagChip';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import {
  BrandAuthorization,
  accountSummary,
  channelsLabel,
  levelLabel,
  verificationStatusOf,
} from '../utils/brandAuthorization';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { businessTypeLabel, businessTypeOptions } from '../utils/businessType';

export const StoreSettingsPage: React.FC = () => {
  const { t } = useAppTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { userProfile } = useAuth();
  const { impersonatedOrg, isImpersonating } = useImpersonation();
  const queryClient = useQueryClient();
  
  const [editBusinessOpen, setEditBusinessOpen] = useState(false);
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [editAddressOpen, setEditAddressOpen] = useState(false);
  const [addDealerOpen, setAddDealerOpen] = useState(false);
  const [editingAuthorization, setEditingAuthorization] = useState<BrandAuthorization | null>(null);
  const [removeDealerTargetId, setRemoveDealerTargetId] = useState<string | null>(null);
  const [brandFilter, setBrandFilter] = useState<'all' | 'verified'>('all');
  const [transferOwnershipOpen, setTransferOwnershipOpen] = useState(false);
  const { showToast } = useToast();
  
  // Ownership transfer form state
  const [selectedNewOwner, setSelectedNewOwner] = useState<any>(null);
  const [confirmationText, setConfirmationText] = useState('');
  
  const [orgData, setOrgData] = useState({
    organizationName: '',
    businessName: '',
    dba: '',
    businessType: '',
  });

  const [contactData, setContactData] = useState({
    primaryContactName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    title: '',
    website: '',
    supportEmail: '',
  });

  const [addressData, setAddressData] = useState({
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
  });

  // Country of the stored address; picks the state/province list and postal format. Not part of the saved payload.
  const [addressCountry, setAddressCountry] = useState('US');

  // Display address state - only updates when saved (used for map)
  const [displayAddressData, setDisplayAddressData] = useState({
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
  });

  const getFullAddress = () => {
    const { streetAddress, city, state, zip } = displayAddressData;
    // Only build URL if we have enough data to geocode
    if (!streetAddress || !city || !state || !zip) return '';
    return `${streetAddress}, ${city}, ${state} ${zip}`;
  };

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const hasGoogleMapsKey = googleMapsApiKey.length > 0;

  const getMapUrl = () => {
    if (!hasGoogleMapsKey) return '';
    const address = getFullAddress();
    if (!address) {
      // Return empty or default view if address is incomplete
      return `https://www.google.com/maps/embed/v1/view?key=${googleMapsApiKey}&center=0,0&zoom=2`;
    }
    return `https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${encodeURIComponent(address)}`;
  };

  // Fetch organization data to get dealer associations
  const impersonatedOrgId = impersonatedOrg?.id;
  const resolvedOrgId =
    impersonatedOrgId ||
    (userProfile as any)?.organizationId ||
    (userProfile as any)?.primaryOrganizationId ||
    null;

  const organizationQueryKey = [
    'organization',
    resolvedOrgId || (isImpersonating ? impersonatedOrgId || 'impersonated' : 'current'),
  ];

  const { data: organizationData, isLoading: orgLoading } = useQuery({
    queryKey: organizationQueryKey,
    queryFn: async () => {
      if (resolvedOrgId) {
        const org = await apiService.getOrganization(resolvedOrgId);
        return org;
      }

      // Fallback: fetch the user's primary organization from the API
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/organizations/me', {
        headers,
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to load organization');
      }

      const data = await response.json();
      return data;
    },
    enabled: !!auth.currentUser,
  });

  const organizationRecord = React.useMemo(
    () => organizationData?.data || organizationData || null,
    [organizationData]
  );
  const effectiveOrgId = resolvedOrgId || organizationRecord?.id || impersonatedOrgId || null;

  // Hydrate UI state from live org data
  React.useEffect(() => {
    const org = organizationRecord;
    if (!org) return;

    const businessInfo = org.businessInfo || {};
    const contactInfo = org.contactInfo || {};
    const primaryContact = contactInfo.primaryContact || contactInfo.primary || contactInfo.contact || {};
    const vendorInfo = org.vendorInfo || {};
    const fallbackName = vendorInfo.companyName || vendorInfo.brandName || businessInfo.businessName || org.organizationName;

    setOrgData({
      organizationName: org.name || org.organizationName || businessInfo.businessName || fallbackName || '',
      businessName: org.businessName || businessInfo.businessName || vendorInfo.companyName || '',
      dba: org.dba || businessInfo.dba || '',
      businessType: org.businessType || businessInfo.businessType || '',
    });
    setContactData({
      primaryContactName: org.primaryContactName || primaryContact.name || contactInfo.name || '',
      primaryContactEmail: org.primaryContactEmail || primaryContact.email || contactInfo.email || '',
      primaryContactPhone: org.primaryContactPhone || primaryContact.phone || contactInfo.phone || '',
      title: org.title || primaryContact.title || '',
      website: org.website || contactInfo.website || businessInfo.website || vendorInfo.website || '',
      supportEmail: org.supportEmail || contactInfo.supportEmail || primaryContact.supportEmail || '',
    });
    const addr =
      org.address ||
      contactInfo.address ||
      businessInfo.address ||
      (primaryContact.address ? primaryContact.address : {}) ||
      vendorInfo.address ||
      {};
    const newAddr = {
      streetAddress: addr.street || addr.streetAddress || '',
      city: addr.city || '',
      state: addr.state || '',
      zip: addr.zip || addr.zipCode || '',
    };
    // Update both edit state and display state when data loads
    setAddressData(newAddr);
    setDisplayAddressData(newAddr);
    setAddressCountry(regionCountryCode(addr.country));
  }, [organizationRecord]);

  // Fetch available brands for dealer associations
  const { data: availableBrands, isLoading: brandsLoading } = useQuery({
    queryKey: ['availableBrands'],
    queryFn: async () => {
      const response = await makeApiCall('/store/brands', {
        headers: await getAuthHeaders(),
      });
      const data = await response.json();
      const brands = data.data || [];
      console.log('📦 Fetched brands:', brands.map((b: any) => ({ id: b.id, name: b.name, logoUrl: b.logoUrl })));
      return brands;
    },
    enabled: !!auth.currentUser,
  });

  // Fetch organization members for ownership transfer
  const { data: organizationMembers, isLoading: membersLoading } = useQuery({
    queryKey: ['organizationMembers', effectiveOrgId || 'current'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/store/members', {
        headers,
      });
      const data = await response.json();
      return data.data || [];
    },
    enabled: !!effectiveOrgId && !!auth.currentUser,
  });

  // Prefer the real org owner from membership; never show the admin's profile while impersonating.
  const organizationOwner = React.useMemo(() => {
    const ownerMember = organizationMembers?.find((member: { role?: string }) => member.role === 'owner');
    if (ownerMember) {
      return {
        displayName: ownerMember.displayName || 'Organization Owner',
        email: ownerMember.email || '',
      };
    }
    if (isImpersonating || membersLoading) {
      return null;
    }
    return {
      displayName: userProfile?.displayName || 'Organization Owner',
      email: userProfile?.email || '',
    };
  }, [organizationMembers, membersLoading, isImpersonating, userProfile]);

  // Brand authorizations (level, channels, proof) for this store
  const { enabled: proofGating } = useOrgFeatureFlag('dealer_proof_gating');
  const { data: brandAuthorizations, isLoading: authorizationsLoading } = useQuery({
    queryKey: [...BRAND_AUTHORIZATIONS_QUERY_KEY, effectiveOrgId],
    queryFn: async (): Promise<BrandAuthorization[]> => {
      const response = await makeApiCall('/store/brand-authorizations', {
        headers: await getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to load brand authorizations');
      const body = await response.json();
      return body?.data || [];
    },
  });

  // Remove dealer association mutation
  const removeDealerMutation = useMutation({
    mutationFn: async (brandId: string) => {
      const response = await makeApiCall(`/store/brand-authorizations/${encodeURIComponent(brandId)}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRAND_AUTHORIZATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: organizationQueryKey });
      queryClient.invalidateQueries({ queryKey: ['availableBrands'] });
      queryClient.invalidateQueries({ queryKey: ['store-organization'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      // The browser's whole-brand-scope query stays fresh for ten minutes (MOT-628);
      // a brand change moves the store's catalog scope, so it is refetched too.
      queryClient.invalidateQueries({ queryKey: ['storeBrandUniverse'] });
      showToast('Brand removed successfully', 'success');
    },
    onError: (error: any) => {
      showToast(error.message || 'Failed to remove brand', 'error');
    },
  });

  // Transfer ownership mutation
  const transferOwnershipMutation = useMutation({
    mutationFn: async (data: { newOwnerId: string; confirmationText: string }) => {
      const response = await makeApiCall('/store/transfer-ownership', {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationQueryKey });
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', userProfile?.organizationId] });
      showToast('Ownership transferred successfully', 'success');
      setTransferOwnershipOpen(false);
      resetTransferForm();
    },
    onError: (error: any) => {
      showToast(error.message || 'Failed to transfer ownership', 'error');
    },
  });

  // Extract and transform dealer associations from live org + brand catalog
  interface TransformedAssociation {
    id: string;
    brand: string;
    logoUrl?: string;
    record: BrandAuthorization;
  }
  const brandCatalog: Array<{ id: string; name: string; logoUrl?: string }> = (availableBrands as any) || [];
  const brandsSoldIds: string[] = (brandAuthorizations || []).map((r) => r.brandId);
  const transformedAssociations: TransformedAssociation[] = (brandAuthorizations || []).map((record) => {
    const brandMeta =
      brandCatalog.find((b) => b.id === record.brandId) ||
      brandCatalog.find((b) => b.id.toLowerCase() === record.brandId.toLowerCase()) ||
      brandCatalog.find((b) => b.name.toLowerCase() === record.brandName.toLowerCase());
    const brandName = brandMeta?.name || record.brandName || record.brandId;
    const logoUrl = resolveBrandLogo(brandName, brandMeta?.logoUrl);
    return { id: record.brandId, brand: brandName, logoUrl: logoUrl || undefined, record };
  });

  const verifiedAssociationCount = transformedAssociations.filter((a) => verificationStatusOf(a.record) === 'verified').length;
  const visibleAssociations = brandFilter === 'verified'
    ? transformedAssociations.filter((a) => verificationStatusOf(a.record) === 'verified')
    : transformedAssociations;

  const handleSave = async (section: string) => {
    try {
      const id = effectiveOrgId;
      if (!id) {
        showToast('Organization ID not found', 'error');
        return;
      }
      
      const headers = await getAuthHeaders();
      const body: any = {};
      
      if (section === 'business') {
        body.name = orgData.organizationName;
        body.businessName = orgData.businessName;
        body.dba = orgData.dba || null;
        body.businessType = orgData.businessType;
      } else if (section === 'contact') {
        body.primaryContactName = contactData.primaryContactName;
        body.primaryContactEmail = contactData.primaryContactEmail;
        body.primaryContactPhone = contactData.primaryContactPhone;
        body.title = contactData.title || null;
        body.website = contactData.website || null;
        body.supportEmail = contactData.supportEmail || null;
      } else if (section === 'address') {
        body.address = {
          streetAddress: addressData.streetAddress,
          street: addressData.streetAddress, // Support both field names
          city: addressData.city,
          state: addressData.state,
          zip: addressData.zip,
        };
      }

      console.log('💾 Saving organization update:', { id, section, body });

      const response = await makeApiCall(`/organizations/${id}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to update organization';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error?.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to update organization');
      }

      console.log('✅ Organization updated successfully');
      
      // Invalidate and refetch
      await queryClient.invalidateQueries({ queryKey: organizationQueryKey });
      await queryClient.refetchQueries({ queryKey: organizationQueryKey });
      
      // Update display address state only after successful save
      if (section === 'address') {
        setDisplayAddressData({ ...addressData });
      }
      
      showToast('Saved successfully', 'success');
      
      if (section === 'business') setEditBusinessOpen(false);
      if (section === 'contact') setEditContactOpen(false);
      if (section === 'address') setEditAddressOpen(false);
    } catch (e: any) {
      console.error('❌ Failed to save organization:', e);
      showToast(e?.message || 'Failed to save', 'error');
    }
  };

  const resetTransferForm = () => {
    setSelectedNewOwner(null);
    setConfirmationText('');
  };

  const handleRemoveDealerAssociation = (associationId: string) => {
    setRemoveDealerTargetId(associationId);
  };

  const handleTransferOwnership = () => {
    if (!selectedNewOwner) {
      showToast('Please select a new owner', 'error');
      return;
    }

    if (confirmationText !== 'TRANSFER OWNERSHIP') {
      showToast('Please type "TRANSFER OWNERSHIP" to confirm', 'error');
      return;
    }

    transferOwnershipMutation.mutate({
      newOwnerId: selectedNewOwner.id,
      confirmationText,
    });
  };

  return (
    <PageContainer>
      <PageHeader title="Store Settings" />
      <Box sx={{ width: '100%' }}>
      <Stack spacing={3}>
        {/* Only when a buying group or head office has invited, or manages, this store. */}
        <GroupInvitationCard />

        {/* Only for an owner or admin, and only while a join request is pending. */}
        <OrganizationJoinRequestsCard />

        <SupportContactCard />

        {/* Dealer Associations Card - moved to top */}
        <Paper 
          sx={{ 
            p: 3, 
            borderRadius: 1.5, 
            boxShadow: '0 6px 16px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VerifiedUser color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Authorized Dealer Associations
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setAddDealerOpen(true)}
              size="small"
              sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
            >
              Add brand
            </Button>
          </Box>

          {transformedAssociations.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t('store.brandAuthorization.verifiedSummary', { verified: verifiedAssociationCount, total: transformedAssociations.length })}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={brandFilter}
                  onChange={(_, v) => { if (v) setBrandFilter(v); }}
                  aria-label={t('store.brandAuthorization.filterLabel')}
                >
                  <ToggleButton value="all">{t('store.brandAuthorization.filterAll')} ({transformedAssociations.length})</ToggleButton>
                  <ToggleButton value="verified">{t('store.brandAuthorization.filterVerified')} ({verifiedAssociationCount})</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          )}

          {proofGating && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('store.brandAuthorization.gatingInfo')}
            </Alert>
          )}
          {orgLoading || authorizationsLoading ? (
            <LoadingState />
          ) : transformedAssociations.length > 0 ? (
            <List disablePadding>
              {visibleAssociations.map((association, index) => {
                const logoUrl = association.logoUrl;
                const brandInfo = getVendorInfo(association.brand);
                return (
                <ListItem key={association.id || index} sx={{ px: 0, py: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
                  <Avatar 
                    src={logoUrl || undefined}
                    sx={{ 
                      bgcolor: logoUrl ? 'white' : brandInfo.color,
                      mr: 2,
                      width: 40,
                      height: 40,
                      border: logoUrl ? '1px solid' : 'none',
                      borderColor: logoUrl ? 'divider' : 'transparent',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                      fontSize: 16,
                      fontWeight: 700,
                      color: 'white',
                    }}
                    imgProps={{
                      style: {
                        objectFit: 'contain',
                        padding: '4px',
                        width: '90%',
                        height: '90%',
                      }
                    }}
                  >
                    {association.brand.charAt(0)}
                  </Avatar>
                  <ListItemText
                    primary={association.brand}
                    secondary={
                      <>
                        <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {[levelLabel(association.record.level), channelsLabel(association.record.channels), accountSummary(association.record)]
                            .filter(Boolean)
                            .join(' · ')}
                        </Box>
                        {verificationStatusOf(association.record) !== 'verified' && association.record.verification?.reviewNotes && (
                          <Box component="span" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                            {t('store.brandAuthorization.reviewNote', { note: association.record.verification.reviewNotes })}
                          </Box>
                        )}
                      </>
                    }
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                    sx={{ minWidth: 0, flex: '1 1 220px' }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 1 auto', minWidth: 0, maxWidth: '100%', pl: { xs: 7, sm: 0 }, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, '& .MuiChip-root': { whiteSpace: 'nowrap', minWidth: { md: 104 } } }}>
                    {(() => {
                      const isUnauthorized = association.record.level === 'unauthorized';
                      const levelChip = (
                        <StatusChip
                          label={levelLabel(association.record.level)}
                          tone={association.record.level === 'authorized_dealer' ? 'positive' : isUnauthorized ? 'caution' : 'neutral'}
                          size="small"
                          icon={association.record.level === 'authorized_dealer' ? <VerifiedUser sx={{ fontSize: 14 }} /> : undefined}
                          sx={{ height: 24, fontSize: '0.7rem' }}
                        />
                      );
                      // An unauthorized brand has nothing to verify: no status chip.
                      if (isUnauthorized) {
                        return (
                          <Tooltip title={t('store.brandAuthorization.unauthorizedHelp')}>
                            <span>{levelChip}</span>
                          </Tooltip>
                        );
                      }
                      return (
                        <>
                          {levelChip}
                          <BrandAuthorizationTagChip record={association.record} />
                        </>
                      );
                    })()}
                    <Tooltip title={t('store.brandAuthorization.editBrand', { brand: association.brand })}>
                      <IconButton
                        size="small"
                        aria-label={t('store.brandAuthorization.editBrand', { brand: association.brand })}
                        onClick={() => setEditingAuthorization(association.record)}
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('store.brandAuthorization.removeBrand', { brand: association.brand })}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('store.brandAuthorization.removeBrand', { brand: association.brand })}
                          onClick={() => handleRemoveDealerAssociation(association.id)}
                          disabled={removeDealerMutation.isPending}
                          color="error"
                        >
                          <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </ListItem>
              );
              })}
              {visibleAssociations.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {t('store.brandAuthorization.noneVerified')}
                </Typography>
              )}
            </List>
          ) : (
            <EmptyState
              size="compact"
              title="No dealer associations configured"
              action={
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setAddDealerOpen(true)}
                  size="small"
                >
                  Add Your First Brand
                </Button>
              }
            />
          )}
        </Paper>
               {/* Business + Contact in two columns on large screens */}
               <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
               <Paper 
                 sx={{ 
                   p: 2.5, 
                   borderRadius: 1.5, 
                   boxShadow: '0 6px 16px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)'
                 }}
               >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Business Information
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => setEditBusinessOpen(true)}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              Edit
            </Button>
          </Box>

                 <Stack spacing={1.25}>
                   <Box>
                     <Typography variant="caption" color="text.secondary" gutterBottom>
                       Organization Name
                     </Typography>
                     <Typography variant="body1" fontWeight={600}>{orgData.organizationName}</Typography>
                   </Box>
                   <Box>
                     <Typography variant="caption" color="text.secondary" gutterBottom>
                       Business Name
                     </Typography>
                     <Typography variant="body1" fontWeight={600}>{orgData.businessName}</Typography>
                   </Box>
                   <Box>
                     <Typography variant="caption" color="text.secondary" gutterBottom>
                       DBA (Doing Business As)
                     </Typography>
                     <Typography variant="body1" fontWeight={600}>{orgData.dba}</Typography>
                   </Box>
                   <Box>
                     <Typography variant="caption" color="text.secondary" gutterBottom>
                       Business Type
                     </Typography>
                     <Typography variant="body1" fontWeight={600}>{businessTypeLabel(orgData.businessType)}</Typography>
                   </Box>
                 </Stack>
               </Paper>

               {/* Contact Information Card */}
               <Paper 
                 sx={{ 
                   p: 2.5, 
                   borderRadius: 1.5, 
                   boxShadow: '0 6px 16px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)'
                 }}
               >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Email color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Contact Information
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => setEditContactOpen(true)}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              Edit
            </Button>
          </Box>

                 <Stack spacing={1.25}>
                   <Box>
                     <Typography variant="caption" color="text.secondary" gutterBottom>Primary Contact Name</Typography>
                     <Typography variant="body1" fontWeight={600}>{contactData.primaryContactName}</Typography>
                   </Box>
                   <Box>
                     <Typography variant="caption" color="text.secondary" gutterBottom>Primary Contact Email</Typography>
                     <Typography variant="body1" fontWeight={600}>{contactData.primaryContactEmail}</Typography>
                   </Box>
                   <Box>
                     <Typography variant="caption" color="text.secondary" gutterBottom>Primary Contact Phone</Typography>
                     <Typography variant="body1" fontWeight={600}>{contactData.primaryContactPhone}</Typography>
                   </Box>
                   <Box>
                     <Typography variant="caption" color="text.secondary" gutterBottom>Title</Typography>
                     <Typography variant="body1" fontWeight={600}>{contactData.title}</Typography>
                   </Box>
                   <Box>
                     <Typography variant="caption" color="text.secondary" gutterBottom>Website</Typography>
                     <Typography variant="body1" fontWeight={600}>{contactData.website}</Typography>
                   </Box>
                   <Box>
                     <Typography variant="caption" color="text.secondary" gutterBottom>Support Email</Typography>
                     <Typography variant="body1" fontWeight={600}>{contactData.supportEmail}</Typography>
                   </Box>
                 </Stack>
                </Paper>
               </Box>

        {/* Address Card */}
               <Paper 
                 sx={{ 
                   p: 2.5, 
                   borderRadius: 1.5, 
                   boxShadow: '0 6px 16px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)'
                 }}
               >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOn color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Address
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => setEditAddressOpen(true)}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              Edit
            </Button>
          </Box>

                 <Box
                   sx={{
                     display: 'grid',
                     gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                     gap: 2,
                   }}
                 >
                   <Box>
                     <Stack spacing={1.25}>
                       <Box>
                         <Typography variant="caption" color="text.secondary" gutterBottom>Street Address</Typography>
                         <Typography variant="body1" fontWeight={600}>{displayAddressData.streetAddress}</Typography>
                       </Box>
                       <Box>
                         <Typography variant="caption" color="text.secondary" gutterBottom>City, State ZIP</Typography>
                         <Typography variant="body1" fontWeight={600}>{`${displayAddressData.city}, ${displayAddressData.state} ${displayAddressData.zip}`}</Typography>
                       </Box>
                       <Box>
                         <Typography variant="caption" color="text.secondary" gutterBottom>Country</Typography>
                         <Typography variant="body1" fontWeight={600}>United States</Typography>
                       </Box>
                     </Stack>
                   </Box>
                  <Box sx={{ borderRadius: 1.5, overflow: 'hidden', boxShadow: '0 6px 16px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)', minHeight: 220 }}>
                     {hasGoogleMapsKey ? (
                       <iframe
                         title="Store Location Map"
                         width="100%"
                         height="100%"
                         style={{ border: 0, minHeight: 220 }}
                         loading="lazy"
                         allowFullScreen
                         referrerPolicy="no-referrer-when-downgrade"
                         src={getMapUrl()}
                       />
                     ) : (
                       <Box 
                         sx={{ 
                           height: 220, 
                           display: 'flex', 
                           flexDirection: 'column',
                           alignItems: 'center', 
                           justifyContent: 'center',
                           bgcolor: tokens.surface.sunken,
                           color: 'text.secondary',
                           p: 2,
                           textAlign: 'center',
                         }}
                       >
                         <LocationOn sx={{ fontSize: 40, color: 'action.disabled', mb: 1 }} />
                         <Typography variant="body2" color="text.secondary">
                           Map preview unavailable
                         </Typography>
                         <Typography variant="caption" color="text.disabled">
                           {getFullAddress() || 'No address configured'}
                         </Typography>
                       </Box>
                     )}
                   </Box>
                 </Box>
        </Paper>

        <StoreLocationsCard />

        <StoreTimezoneCard />

        <PriceChangeReviewCard />

        <RebateTreatmentCard />

        <BrandPricingExclusionsCard />

        <TradeInCreditsCard />

        <StoreAiCopyRulesCard />

        <SellThroughSharingCard />

        <ReorderSettingsCard />

        {/* Organization Owner Card */}
        <Paper 
          sx={{ 
            p: 3, 
            borderRadius: 2, 
            boxShadow: '0 6px 16px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Badge color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Organization Owner
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<TransferWithinAStation />}
              onClick={() => setTransferOwnershipOpen(true)}
              size="small"
              sx={{ borderRadius: 2 }}
              color="warning"
            >
              Transfer ownership
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {membersLoading && !organizationOwner ? (
              <CircularProgress size={28} />
            ) : (
              <>
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                  {organizationOwner?.displayName?.charAt(0) ||
                    organizationOwner?.email?.charAt(0) ||
                    'U'}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {organizationOwner?.displayName || 'Organization Owner'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {organizationOwner?.email || '—'}
                  </Typography>
                  <Chip label="Owner" color="primary" size="small" sx={{ mt: 1 }} />
                </Box>
              </>
            )}
          </Box>
        </Paper>
      </Stack>
      </Box>

      {/* Edit Business Dialog */}
      <FormDialog
        open={editBusinessOpen}
        onClose={() => setEditBusinessOpen(false)}
        title="Edit Business Information"
        onSubmit={() => handleSave('business')}
        submitLabel="Save Changes"
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Organization Name"
            value={orgData.organizationName}
            onChange={(e) => setOrgData({ ...orgData, organizationName: e.target.value })}
          />
          <TextField
            fullWidth
            label="Business Name"
            value={orgData.businessName}
            onChange={(e) => setOrgData({ ...orgData, businessName: e.target.value })}
          />
          <TextField
            fullWidth
            label="DBA (Doing Business As)"
            value={orgData.dba}
            onChange={(e) => setOrgData({ ...orgData, dba: e.target.value })}
          />
          <FormControl fullWidth>
            <InputLabel>Business Type</InputLabel>
            <Select
              value={orgData.businessType}
              label="Business Type"
              onChange={(e) => setOrgData({ ...orgData, businessType: e.target.value })}
            >
              {businessTypeOptions(orgData.businessType).map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </FormDialog>

      {/* Edit Contact Dialog */}
      <FormDialog
        open={editContactOpen}
        onClose={() => setEditContactOpen(false)}
        title="Edit Contact Information"
        onSubmit={() => handleSave('contact')}
        submitLabel="Save Changes"
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Primary Contact Name"
            value={contactData.primaryContactName}
            onChange={(e) => setContactData({ ...contactData, primaryContactName: e.target.value })}
          />
          <ValidatedTextField
            fullWidth
            label="Primary Contact Email"
            value={contactData.primaryContactEmail}
            onChange={(e) => setContactData({ ...contactData, primaryContactEmail: e.target.value })}
            validate={validateEmail}
          />
          <ValidatedTextField
            fullWidth
            label="Primary Contact Phone"
            value={contactData.primaryContactPhone}
            onChange={(e) => setContactData({ ...contactData, primaryContactPhone: e.target.value })}
            validate={validatePhone}
            normalize={normalizePhone}
            onNormalized={(v) => setContactData({ ...contactData, primaryContactPhone: v })}
          />
          <TextField
            fullWidth
            label="Title"
            value={contactData.title}
            onChange={(e) => setContactData({ ...contactData, title: e.target.value })}
          />
          <ValidatedTextField
            fullWidth
            label="Website"
            value={contactData.website}
            onChange={(e) => setContactData({ ...contactData, website: e.target.value })}
            validate={(v) => validateUrl(v, { allowSchemeless: true })}
          />
          <ValidatedTextField
            fullWidth
            label="Support Email"
            value={contactData.supportEmail}
            onChange={(e) => setContactData({ ...contactData, supportEmail: e.target.value })}
            validate={validateEmail}
          />
        </Stack>
      </FormDialog>

      {/* Edit Address Dialog */}
      <FormDialog
        open={editAddressOpen}
        onClose={() => {
          // Reset to display data when canceling
          setAddressData({ ...displayAddressData });
          setEditAddressOpen(false);
        }}
        title="Edit Address"
        onSubmit={() => handleSave('address')}
        submitLabel="Save Changes"
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Street Address"
            value={addressData.streetAddress}
            onChange={(e) => setAddressData({ ...addressData, streetAddress: e.target.value })}
          />
          <TextField
            fullWidth
            label="City"
            value={addressData.city}
            onChange={(e) => setAddressData({ ...addressData, city: e.target.value })}
          />
          <RegionSelect
            country={addressCountry}
            value={addressData.state}
            onChange={(v) => setAddressData({ ...addressData, state: v })}
            size="medium"
          />
          <ValidatedTextField
            fullWidth
            label="ZIP Code"
            value={addressData.zip}
            onChange={(e) => setAddressData({ ...addressData, zip: e.target.value })}
            validate={(v) => validatePostalCode(v, addressCountry)}
            normalize={(v) => normalizePostalCode(v, addressCountry)}
            onNormalized={(v) => setAddressData({ ...addressData, zip: v })}
          />
        </Stack>
      </FormDialog>

      <BrandAuthorizationDialog
        open={addDealerOpen || editingAuthorization !== null}
        onClose={() => {
          setAddDealerOpen(false);
          setEditingAuthorization(null);
        }}
        organizationId={effectiveOrgId}
        existing={editingAuthorization}
        brandOptions={brandCatalog}
        brandsLoading={brandsLoading}
        takenBrandIds={brandsSoldIds}
      />

      {/* Transfer Ownership Dialog */}
      <Dialog
        open={transferOwnershipOpen}
        onClose={() => setTransferOwnershipOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'warning.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning />
            Transfer Organization Ownership
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The new owner will have full control over the organization.
          </Alert>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={organizationMembers?.filter((member: any) => member.role !== 'owner') || []}
              getOptionLabel={(option: any) => `${option.displayName} (${option.email})`}
              value={selectedNewOwner}
              onChange={(event, newValue) => setSelectedNewOwner(newValue)}
              loading={membersLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select New Owner"
                  placeholder="Choose a member to transfer ownership to..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {membersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              fullWidth
              label="Confirmation"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="Type 'TRANSFER OWNERSHIP' to confirm"
              helperText="You must type 'TRANSFER OWNERSHIP' exactly to proceed"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTransferOwnershipOpen(false)} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={handleTransferOwnership} 
            startIcon={<TransferWithinAStation />}
            disabled={transferOwnershipMutation.isPending || confirmationText !== 'TRANSFER OWNERSHIP'}
          >
            {transferOwnershipMutation.isPending ? <CircularProgress size={20} /> : 'Transfer Ownership'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove brand confirmation */}
      <ConfirmDialog
        open={removeDealerTargetId !== null}
        title="Remove Brand"
        message="Are you sure you want to remove this brand from your list?"
        confirmLabel="Remove"
        confirmColor="error"
        loading={removeDealerMutation.isPending}
        onConfirm={() => {
          if (removeDealerTargetId) {
            removeDealerMutation.mutate(removeDealerTargetId, { onSettled: () => setRemoveDealerTargetId(null) });
          }
        }}
        onClose={() => setRemoveDealerTargetId(null)}
      />

    </PageContainer>
  );
};

export default StoreSettingsPage;
